import React, { useState, useEffect, useCallback } from "react";
import api, { fmtVND, fmtFull } from "@/lib/api";
import { Loader2, TrendingUp, TrendingDown, BarChart3, DollarSign, CreditCard, Truck, Wallet, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { DateRangePicker } from "@/components/DateRangePicker";
import QuickDateButtons from "./QuickDateButtons";
import ApexChart from "react-apexcharts";

export default function BaoCaoKDTab() {
  // Default to current month start-end
  const [start, setStart] = useState(() => {
    const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-01`;
  });
  const [end, setEnd] = useState(() => new Date().toISOString().slice(0, 10));
  const [subTab, setSubTab] = useState("overview");
  const [view, setView] = useState("detail");
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [grabData, setGrabData] = useState(null);
  const [cardData, setCardData] = useState(null);
  const [dtData, setDtData] = useState(null);
  const [sqData, setSqData] = useState(null);
  const [kcData, setKcData] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, gr, cd, dt, sq, kc] = await Promise.all([
        api.get(`/accounting/bao-cao-kd/overview?start=${start}&end=${end}`),
        api.get(`/accounting/grab?start=${start}&end=${end}&view=${view}`),
        api.get(`/accounting/quet-the?start=${start}&end=${end}&view=${view}`),
        api.get(`/accounting/so-thu-chi?start=${start}&end=${end}&view=${view}`),
        api.get(`/accounting/so-thu-chi?start=${start}&end=${end}&view=summary`),
        api.get(`/accounting/bang-ke-doanh-thu?start=${start}&end=${end}&view=${view}`),
      ]);
      setOverview(ov.data);
      setGrabData(gr.data);
      setCardData(cd.data);
      setDtData(dt.data);
      setSqData(sq.data);
      setKcData(kc.data);
    } catch {}
    finally { setLoading(false); }
  }, [start, end, view]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  // If overview shows 0, try loading month 3
  useEffect(() => {
    if (overview && overview.grand_total_revenue === 0 && start.includes("2026-04")) {
      setStart("2026-03-01");
      setEnd("2026-03-31");
    }
  }, [overview, start]);

  const BCKD_TABS = [
    { key: "overview", label: "Tổng quan", icon: BarChart3, color: "text-indigo-400" },
    { key: "doanh_thu", label: "Doanh thu", icon: DollarSign, color: "text-emerald-400" },
    { key: "so_quy", label: "Sổ quỹ", icon: Wallet, color: "text-amber-400" },
    { key: "ket_ca", label: "BC Kết ca", icon: TrendingUp, color: "text-sky-400" },
    { key: "grab", label: "GRAB", icon: Truck, color: "text-lime-400" },
    { key: "quet_the", label: "Quẹt thẻ", icon: CreditCard, color: "text-violet-400" },
  ];

  const hasData = overview && overview.grand_total_revenue > 0;

  return (
    <div className="space-y-4" data-testid="acct-bao-cao-kd">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-sm font-bold text-slate-200 flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-indigo-400" /> Báo Cáo Kinh Doanh
        </h2>
        <div className="flex items-center gap-2">
          <DateRangePicker start={start} end={end} onChangeStart={setStart} onChangeEnd={setEnd} />
          <QuickDateButtons setStart={setStart} setEnd={setEnd} />
        </div>
      </div>

      {/* Sub-tabs with icons */}
      <div className="flex gap-1 bg-[#0f172a] border border-[#334155] rounded-lg p-1">
        {BCKD_TABS.map(t => (
          <button key={t.key} onClick={() => setSubTab(t.key)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-[10px] font-medium transition-all ${subTab === t.key ? "bg-[#1e293b] text-slate-100 shadow border border-[#475569]" : "text-slate-500 hover:text-slate-300"}`}>
            <t.icon className={`w-3 h-3 ${subTab === t.key ? t.color : ""}`} />
            {t.label}
          </button>
        ))}
      </div>

      {loading ? <Loader2 className="w-5 h-5 text-indigo-400 animate-spin mx-auto mt-8" /> : !hasData ? (
        <div className="text-center py-16 bg-[#1e293b] border border-[#334155] rounded-xl">
          <BarChart3 className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400 text-sm">Chưa có dữ liệu trong khoảng thời gian này</p>
          <p className="text-slate-600 text-xs mt-1">Thử chọn khoảng ngày khác hoặc import file Báo cáo KD</p>
        </div>
      ) : (
        <>
          {/* ═══ OVERVIEW ═══ */}
          {subTab === "overview" && overview && (
            <div className="space-y-4">
              {/* Hero KPI row */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {[
                  { label: "DT Kết ca", value: overview.ket_ca?.dt_ket_ca, color: "from-[#4680ff] to-[#5b8def]", icon: TrendingUp },
                  { label: "DT KiotViet", value: overview.ket_ca?.dt_kiot, color: "from-[#0891b2] to-[#06b6d4]", icon: BarChart3 },
                  { label: "GRAB", value: overview.grab?.total_con_lai, color: "from-[#16a34a] to-[#22c55e]", icon: Truck },
                  { label: "Quẹt thẻ", value: overview.card?.total, color: "from-[#7c3aed] to-[#a78bfa]", icon: CreditCard },
                  { label: "TỔNG DOANH THU", value: overview.grand_total_revenue, color: "from-[#dc2626] to-[#f87171]", icon: DollarSign },
                ].map((kpi, i) => (
                  <div key={i} className={`relative overflow-hidden rounded-xl bg-gradient-to-br ${kpi.color} p-4 shadow-lg`}>
                    <div className="flex items-center gap-1.5 mb-1">
                      <kpi.icon className="w-3.5 h-3.5 text-white/70" />
                      <span className="text-[9px] text-white/70 uppercase font-bold tracking-wider">{kpi.label}</span>
                    </div>
                    <p className="text-xl font-bold text-white">{fmtVND(kpi.value || 0)}</p>
                  </div>
                ))}
              </div>

              {/* Sổ quỹ + Chênh lệch */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="bg-[#1e293b] border border-emerald-500/20 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <ArrowUpRight className="w-4 h-4 text-emerald-400" />
                    <span className="text-[10px] text-slate-400 uppercase font-bold">Sổ quỹ - Thu</span>
                  </div>
                  <p className="text-lg font-bold text-emerald-400">{fmtFull(overview.so_quy?.total_thu || 0)}</p>
                </div>
                <div className="bg-[#1e293b] border border-red-500/20 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <ArrowDownRight className="w-4 h-4 text-red-400" />
                    <span className="text-[10px] text-slate-400 uppercase font-bold">Sổ quỹ - Chi</span>
                  </div>
                  <p className="text-lg font-bold text-red-400">{fmtFull(overview.so_quy?.total_chi || 0)}</p>
                </div>
                <div className={`bg-[#1e293b] border ${(overview.ket_ca?.chenh_lech || 0) >= 0 ? "border-emerald-500/20" : "border-red-500/20"} rounded-xl p-4`}>
                  <span className="text-[10px] text-slate-400 uppercase font-bold">Chênh lệch Kết ca vs Kiot</span>
                  <p className={`text-lg font-bold mt-1 ${(overview.ket_ca?.chenh_lech || 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>{fmtFull(overview.ket_ca?.chenh_lech || 0)}</p>
                </div>
              </div>
            </div>
          )}

          {/* ═══ DOANH THU ═══ */}
          {subTab === "doanh_thu" && dtData && (
            <div className="space-y-3">
              {/* KPI summary */}
              <div className="grid grid-cols-4 gap-3">
                <div className="bg-[#1e293b] border border-sky-500/20 rounded-xl p-3">
                  <p className="text-[9px] text-sky-300 uppercase font-bold">Tồn đầu kỳ</p>
                  <p className="text-sm font-bold text-sky-400">{fmtFull(dtData.ton_dau_ky || 0)}</p>
                </div>
                <div className="bg-[#1e293b] border border-emerald-500/20 rounded-xl p-3">
                  <p className="text-[9px] text-emerald-300 uppercase font-bold">Tổng Thu</p>
                  <p className="text-sm font-bold text-emerald-400">{fmtFull(dtData.total_thu || 0)}</p>
                </div>
                <div className="bg-[#1e293b] border border-red-500/20 rounded-xl p-3">
                  <p className="text-[9px] text-red-300 uppercase font-bold">Tổng Chi</p>
                  <p className="text-sm font-bold text-red-400">{fmtFull(dtData.total_chi || 0)}</p>
                </div>
                <div className="bg-[#1e293b] border border-amber-500/20 rounded-xl p-3">
                  <p className="text-[9px] text-amber-300 uppercase font-bold">Tồn cuối kỳ</p>
                  <p className="text-sm font-bold text-amber-400">{fmtFull(dtData.ton_cuoi_ky || 0)}</p>
                </div>
              </div>
              {/* Table */}
              <div className="bg-[#1e293b] border border-[#334155] rounded-xl overflow-x-auto">
                <table className="w-full text-[10px] min-w-[700px]">
                  <thead>
                    <tr style={{ background: "linear-gradient(135deg, #0c1524, #0f172a)" }} className="border-b border-[#475569]">
                      <th className="py-2.5 px-3 text-center text-slate-500 w-10">STT</th>
                      <th className="py-2.5 px-3 text-left text-slate-400">Ngày</th>
                      <th className="py-2.5 px-3 text-left text-slate-400">Nội dung</th>
                      <th className="py-2.5 px-3 text-right text-emerald-400">Thu</th>
                      <th className="py-2.5 px-3 text-right text-red-400">Chi</th>
                      <th className="py-2.5 px-3 text-right text-amber-400">Tồn</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(dtData.rows || []).slice(0, 200).map((r, i) => (
                      r.is_total ? (
                        <tr key={i} className="border-t-2 border-[#475569]" style={{ background: "rgba(99,102,241,0.05)" }}>
                          <td colSpan={3} className="py-2 px-3 font-bold text-indigo-300">{r.description}</td>
                          <td className="py-2 px-3 text-right font-bold text-emerald-400">{r.thu > 0 ? fmtFull(r.thu) : ""}</td>
                          <td className="py-2 px-3 text-right font-bold text-red-400">{r.chi > 0 ? fmtFull(r.chi) : ""}</td>
                          <td className="py-2 px-3 text-right font-bold text-amber-400">{fmtFull(r.ton)}</td>
                        </tr>
                      ) : (
                        <tr key={i} className={`border-t border-[#1e293b] hover:bg-[#1e293b] ${i % 2 === 0 ? "bg-[#0f172a]" : "bg-[#131d2e]"}`}>
                          <td className="py-1.5 px-3 text-center text-slate-600">{r.stt}</td>
                          <td className="py-1.5 px-3 text-slate-400 font-mono text-[9px]">{r.date?.slice(5)}</td>
                          <td className="py-1.5 px-3 text-slate-300 truncate max-w-[250px]">{r.noi_dung}</td>
                          <td className="py-1.5 px-3 text-right text-emerald-400">{r.thu > 0 ? fmtFull(r.thu) : ""}</td>
                          <td className="py-1.5 px-3 text-right text-red-400">{r.chi > 0 ? fmtFull(r.chi) : ""}</td>
                          <td className="py-1.5 px-3 text-right text-slate-200 font-medium">{fmtFull(r.ton)}</td>
                        </tr>
                      )
                    ))}
                    {(!dtData.rows?.length) && <tr><td colSpan={6} className="py-8 text-center text-slate-500">Chưa có dữ liệu</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ═══ SỔ QUỸ ═══ */}
          {subTab === "so_quy" && sqData && (
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl overflow-hidden">
              <table className="w-full text-[10px]">
                <thead>
                  <tr style={{ background: "linear-gradient(135deg, #0c1524, #0f172a)" }} className="border-b border-[#475569]">
                    <th className="py-2.5 px-3 text-left text-slate-400">Ngày</th>
                    <th className="py-2.5 px-3 text-right text-emerald-400">Thu</th>
                    <th className="py-2.5 px-3 text-right text-red-400">Chi</th>
                    <th className="py-2.5 px-3 text-right text-amber-400 font-bold">Tồn</th>
                    <th className="py-2.5 px-3 text-center text-slate-400">GD</th>
                  </tr>
                </thead>
                <tbody>
                  {(sqData.rows || []).map((r, i) => (
                    <tr key={i} className={`border-t border-[#1e293b] hover:bg-[#1e293b] ${i % 2 === 0 ? "bg-[#0f172a]" : "bg-[#131d2e]"}`}>
                      <td className="py-2 px-3 text-slate-300 font-mono">{r.date}</td>
                      <td className="py-2 px-3 text-right text-emerald-400 font-medium">{r.thu > 0 ? fmtFull(r.thu) : ""}</td>
                      <td className="py-2 px-3 text-right text-red-400 font-medium">{r.chi > 0 ? fmtFull(r.chi) : ""}</td>
                      <td className="py-2 px-3 text-right text-amber-400 font-bold">{fmtFull(r.ton)}</td>
                      <td className="py-2 px-3 text-center text-slate-500">{r.count}</td>
                    </tr>
                  ))}
                  {(!sqData.rows?.length) && <tr><td colSpan={5} className="py-8 text-center text-slate-500">Chưa có dữ liệu</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {/* ═══ BC KẾT CA ═══ */}
          {subTab === "ket_ca" && kcData && (
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl overflow-x-auto">
              <table className="w-full text-[9px] min-w-[1100px]">
                <thead>
                  <tr style={{ background: "linear-gradient(135deg, #0c1524, #0f172a)" }} className="border-b border-[#475569]">
                    <th className="py-2.5 px-2 text-left text-slate-400">Ngày</th>
                    <th className="py-2.5 px-2 text-left text-slate-400">Khu</th>
                    <th className="py-2.5 px-2 text-right text-sky-400">DT Kết ca</th>
                    <th className="py-2.5 px-2 text-right text-sky-400">DT Kiot</th>
                    <th className="py-2.5 px-2 text-right text-slate-400">CL</th>
                    <th className="py-2.5 px-2 text-right text-emerald-400">TM Kết ca</th>
                    <th className="py-2.5 px-2 text-right text-emerald-400">TM Kiot</th>
                    <th className="py-2.5 px-2 text-right text-indigo-400">VNPay</th>
                    <th className="py-2.5 px-2 text-right text-violet-400">Momo</th>
                    <th className="py-2.5 px-2 text-right text-lime-400">Grab</th>
                    <th className="py-2.5 px-2 text-right text-amber-400">Quẹt thẻ</th>
                    <th className="py-2.5 px-2 text-right text-cyan-400">CK Kết ca</th>
                    <th className="py-2.5 px-2 text-right text-cyan-400">CK Kiot</th>
                  </tr>
                </thead>
                <tbody>
                  {(kcData.rows || []).map((r, i) => (
                    r.is_total ? (
                      <tr key={i} className="border-t-2 border-[#475569]" style={{ background: "rgba(99,102,241,0.06)" }}>
                        <td className="py-2 px-2 font-bold text-indigo-300">{r.date?.slice(5)}</td>
                        <td className="py-2 px-2 font-bold text-slate-300">{r.area}</td>
                        <td className="py-2 px-2 text-right font-bold text-sky-300">{r.dt_ket_ca ? fmtFull(r.dt_ket_ca) : ""}</td>
                        <td className="py-2 px-2 text-right font-bold text-sky-300">{r.dt_kiot ? fmtFull(r.dt_kiot) : ""}</td>
                        <td className={`py-2 px-2 text-right font-bold ${(r.chenh_lech_dt||0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>{r.chenh_lech_dt ? fmtFull(r.chenh_lech_dt) : ""}</td>
                        <td className="py-2 px-2 text-right font-bold text-emerald-300">{r.tm_ket_ca ? fmtFull(r.tm_ket_ca) : ""}</td>
                        <td className="py-2 px-2 text-right font-bold text-emerald-300">{r.tm_kiot ? fmtFull(r.tm_kiot) : ""}</td>
                        <td className="py-2 px-2 text-right font-bold text-indigo-300">{r.vnpay ? fmtFull(r.vnpay) : ""}</td>
                        <td className="py-2 px-2 text-right font-bold text-violet-300">{r.momo ? fmtFull(r.momo) : ""}</td>
                        <td className="py-2 px-2 text-right font-bold text-lime-300">{r.grab ? fmtFull(r.grab) : ""}</td>
                        <td className="py-2 px-2 text-right font-bold text-amber-300">{r.quet_the ? fmtFull(r.quet_the) : ""}</td>
                        <td className="py-2 px-2 text-right font-bold text-cyan-300">{r.ck_ket_ca ? fmtFull(r.ck_ket_ca) : ""}</td>
                        <td className="py-2 px-2 text-right font-bold text-cyan-300">{r.ck_kiot ? fmtFull(r.ck_kiot) : ""}</td>
                      </tr>
                    ) : (
                      <tr key={i} className={`border-t border-[#1e293b] hover:bg-[#1e293b] ${i % 2 === 0 ? "bg-[#0f172a]" : "bg-[#131d2e]"}`}>
                        <td className="py-1.5 px-2 text-slate-400 font-mono text-[8px]">{r.date?.slice(5)}</td>
                        <td className="py-1.5 px-2 text-slate-200 font-medium">{r.area}</td>
                        <td className="py-1.5 px-2 text-right text-sky-300">{r.dt_ket_ca ? fmtFull(r.dt_ket_ca) : ""}</td>
                        <td className="py-1.5 px-2 text-right text-sky-300/70">{r.dt_kiot ? fmtFull(r.dt_kiot) : ""}</td>
                        <td className={`py-1.5 px-2 text-right font-medium ${(r.chenh_lech_dt||0) === 0 ? "text-slate-600" : (r.chenh_lech_dt||0) > 0 ? "text-emerald-400" : "text-red-400"}`}>{r.chenh_lech_dt ? fmtFull(r.chenh_lech_dt) : ""}</td>
                        <td className="py-1.5 px-2 text-right text-emerald-400/80">{r.tm_ket_ca ? fmtFull(r.tm_ket_ca) : ""}</td>
                        <td className="py-1.5 px-2 text-right text-emerald-400/60">{r.tm_kiot ? fmtFull(r.tm_kiot) : ""}</td>
                        <td className="py-1.5 px-2 text-right text-indigo-400">{r.vnpay ? fmtFull(r.vnpay) : ""}</td>
                        <td className="py-1.5 px-2 text-right text-violet-400">{r.momo ? fmtFull(r.momo) : ""}</td>
                        <td className="py-1.5 px-2 text-right text-lime-400">{r.grab ? fmtFull(r.grab) : ""}</td>
                        <td className="py-1.5 px-2 text-right text-amber-400">{r.quet_the ? fmtFull(r.quet_the) : ""}</td>
                        <td className="py-1.5 px-2 text-right text-cyan-400/80">{r.ck_ket_ca ? fmtFull(r.ck_ket_ca) : ""}</td>
                        <td className="py-1.5 px-2 text-right text-cyan-400/60">{r.ck_kiot ? fmtFull(r.ck_kiot) : ""}</td>
                      </tr>
                    )
                  ))}
                  {(!kcData.rows?.length) && <tr><td colSpan={13} className="py-8 text-center text-slate-500">Chưa có dữ liệu BC Kết ca</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {/* ═══ GRAB ═══ */}
          {subTab === "grab" && grabData && (
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl overflow-hidden">
              {/* Grab summary */}
              {grabData.rows?.length > 0 && (
                <div className="grid grid-cols-3 gap-3 p-3 border-b border-[#334155]">
                  <div className="bg-[#0f172a] rounded-lg p-2.5">
                    <p className="text-[8px] text-slate-500 uppercase">Tổng bill</p>
                    <p className="text-sm font-bold text-amber-400">{fmtFull(grabData.total_bill || 0)}</p>
                  </div>
                  <div className="bg-[#0f172a] rounded-lg p-2.5">
                    <p className="text-[8px] text-slate-500 uppercase">Chiết khấu</p>
                    <p className="text-sm font-bold text-red-400">-{fmtFull((grabData.total_bill || 0) - (grabData.total_con_lai || 0))}</p>
                  </div>
                  <div className="bg-[#0f172a] rounded-lg p-2.5">
                    <p className="text-[8px] text-slate-500 uppercase">Thực nhận</p>
                    <p className="text-sm font-bold text-lime-400">{fmtFull(grabData.total_con_lai || 0)}</p>
                  </div>
                </div>
              )}
              <table className="w-full text-[10px]">
                <thead>
                  <tr style={{ background: "linear-gradient(135deg, #0c1524, #0f172a)" }} className="border-b border-[#475569]">
                    <th className="py-2.5 px-3 text-left text-slate-400">Ngày</th>
                    <th className="py-2.5 px-3 text-right text-orange-400">Gà rán</th>
                    <th className="py-2.5 px-3 text-right text-cyan-400">Khu café</th>
                    <th className="py-2.5 px-3 text-right text-amber-400 font-bold">Tổng bill</th>
                    <th className="py-2.5 px-3 text-right text-red-400">Chiết khấu 20%</th>
                    <th className="py-2.5 px-3 text-right text-lime-400 font-bold">Còn lại</th>
                  </tr>
                </thead>
                <tbody>
                  {(grabData.rows || []).map((r, i) => (
                    <tr key={i} className={`border-t border-[#1e293b] hover:bg-[#1e293b] ${i % 2 === 0 ? "bg-[#0f172a]" : "bg-[#131d2e]"}`}>
                      <td className="py-1.5 px-3 text-slate-300 font-mono text-[9px]">{r.date?.slice(5)}</td>
                      <td className="py-1.5 px-3 text-right text-orange-400">{r.ga_ran ? fmtFull(r.ga_ran) : ""}</td>
                      <td className="py-1.5 px-3 text-right text-cyan-400">{r.khu_cafe ? fmtFull(r.khu_cafe) : ""}</td>
                      <td className="py-1.5 px-3 text-right text-amber-400 font-medium">{fmtFull(r.tong_bill || 0)}</td>
                      <td className="py-1.5 px-3 text-right text-red-400">{r.chiet_khau ? `-${fmtFull(r.chiet_khau)}` : ""}</td>
                      <td className="py-1.5 px-3 text-right text-lime-400 font-bold">{fmtFull(r.con_lai || 0)}</td>
                    </tr>
                  ))}
                  {(!grabData.rows?.length) && <tr><td colSpan={6} className="py-8 text-center text-slate-500">Chưa có dữ liệu GRAB</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {/* ═══ QUẸT THẺ ═══ */}
          {subTab === "quet_the" && cardData && (
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl overflow-hidden">
              {/* Card summary */}
              {cardData.rows?.length > 0 && (
                <div className="p-3 border-b border-[#334155] flex items-center justify-between">
                  <span className="text-xs text-slate-400">Tổng quẹt thẻ:</span>
                  <span className="text-lg font-bold text-violet-400">{fmtFull(cardData.grand_total || 0)}</span>
                </div>
              )}
              <table className="w-full text-[10px]">
                <thead>
                  <tr style={{ background: "linear-gradient(135deg, #0c1524, #0f172a)" }} className="border-b border-[#475569]">
                    <th className="py-2.5 px-3 text-left text-slate-400">Ngày</th>
                    <th className="py-2.5 px-3 text-right text-sky-400">Visa</th>
                    <th className="py-2.5 px-3 text-right text-amber-400">Amex</th>
                    <th className="py-2.5 px-3 text-right text-violet-400">Master</th>
                    <th className="py-2.5 px-3 text-right text-emerald-400">Thẻ ghi nợ</th>
                    <th className="py-2.5 px-3 text-right text-slate-200 font-bold">Tổng</th>
                  </tr>
                </thead>
                <tbody>
                  {(cardData.rows || []).map((r, i) => (
                    <tr key={i} className={`border-t border-[#1e293b] hover:bg-[#1e293b] ${i % 2 === 0 ? "bg-[#0f172a]" : "bg-[#131d2e]"}`}>
                      <td className="py-1.5 px-3 text-slate-300 font-mono text-[9px]">{r.date?.slice(5)}</td>
                      <td className="py-1.5 px-3 text-right text-sky-400">{r.visa ? fmtFull(r.visa) : ""}</td>
                      <td className="py-1.5 px-3 text-right text-amber-400">{r.amex ? fmtFull(r.amex) : ""}</td>
                      <td className="py-1.5 px-3 text-right text-violet-400">{r.master ? fmtFull(r.master) : ""}</td>
                      <td className="py-1.5 px-3 text-right text-emerald-400">{r.ghi_no ? fmtFull(r.ghi_no) : ""}</td>
                      <td className="py-1.5 px-3 text-right text-slate-100 font-bold">{fmtFull(r.total || 0)}</td>
                    </tr>
                  ))}
                  {(!cardData.rows?.length) && <tr><td colSpan={6} className="py-8 text-center text-slate-500">Chưa có dữ liệu quẹt thẻ</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
